import {
  Component, ElementRef, EventEmitter, Input, NgZone, OnInit, Output,
  ViewChild,
} from '@angular/core';
import { Campo } from 'src/app/models/coleta';
import { HandshakeService } from 'src/app/services/handshake.service';
import { take } from 'rxjs/operators';
import { FortFaceDataDocument } from 'src/app/models/fortface-data';
import { SessionStorageService } from 'src/app/services/session-storage.service';
import { DEFAULT_PHOTO } from './img/photo';

/**
 * Mensagens amigáveis em PT-BR para os códigos de erro retornados pelo
 * callback do startSessionDoc (v2.4.0+).
 */
const SESSION_ERROR_MESSAGES: Record<string, string> = {
  notInitialized: 'A captura não foi inicializada corretamente. Tente novamente.',
  cameraDisconnected: 'A câmera foi desconectada durante a captura. Verifique sua câmera e tente novamente.',
  noCompatibleVideoDevice: 'Nenhuma câmera compatível foi encontrada no dispositivo.',
  cameraPermissionDenied: 'Permissão de acesso à câmera negada. Habilite o acesso à câmera nas configurações do navegador.',
  cameraUnavailable: 'A câmera está indisponível. Verifique se ela não está sendo usada por outro aplicativo.',
  cameraAccessPolicyRestricted: 'Acesso à câmera bloqueado por política de segurança do navegador.',
  cameraAccessUnexpectedError: 'Erro inesperado ao acessar a câmera. Tente novamente.',
  browserUnsupported: 'Seu navegador não é compatível com a captura de documentos. Atualize ou use outro navegador.',
  browserValidationUnexpectedError: 'Erro inesperado ao validar o navegador. Tente novamente.'
};

/**
 * Mensagens amigáveis em PT-BR para os erros lançados pelo método startDoc()
 * (v2.4.0+).
 */
const START_ERROR_MESSAGES: Record<string, string> = {
  FORTFACE_INSECURE_PROTOCOL: 'A captura de documentos exige uma conexão segura (HTTPS).',
  FORTFACE_GENERATE_DEVICE_REQUEST_INFO_ERROR: 'Falha ao preparar a captura de documentos. Tente novamente.',
  FORTFACE_INTERNAL_SETUP_ERROR: 'Falha ao inicializar a captura de documentos. Tente novamente.',
  FORTFACE_UNEXPECTED_START_ERROR: 'Erro inesperado ao iniciar a captura de documentos. Tente novamente.'
};

@Component({
  selector: 'app-image-capture-fortface-document',
  templateUrl: './image-capture-fortface-document.component.html',
  styleUrls: ['./image-capture-fortface-document.component.scss']
})
export class ImageCaptureFortFaceDocumentComponent implements OnInit {

  @Output()
  imageCapture = new EventEmitter<FortFaceDataDocument>();

  @Output()
  captureError = new EventEmitter<string>();

  @Input() isSelf: Boolean = false;

  @Input() exibirTelaCaptura: Boolean = false;

  @Input() campo: Campo = {};

  @Output()
  isLoading = new EventEmitter<Boolean>();

  @ViewChild('forfacesdk', { static: true }) sdk: ElementRef<FortFaceSdk.Components.FortfaceSdk> | undefined;

  private sessionId: string = "";

  constructor(private handshakeService: HandshakeService, private ngZone: NgZone, private sessionStorageService: SessionStorageService) {
  }

  async ngOnInit() {
    await this.initializeFortfaceSdk();
  }

  async initializeFortfaceSdk() {
    if (!this.sdk) return;

    this.ngZone.runOutsideAngular(async () => {
      try {
        // A partir da v2.4.0, o startDoc() não solicita permissão de câmera
        // nem valida o navegador — isso é feito apenas no startSessionDoc().
        const deviceRequestInfo = await this.sdk!.nativeElement.startDoc();

        this.handshakeService.performHandshake(deviceRequestInfo, FortFaceSdk.FortfaceActionEnum.DOCUMENT)
          .pipe(take(1))
          .subscribe(
            (response) => {
              this.ngZone.run(() => {
                if (!this.sdk) throw new Error("SDK not found");
                this.sessionId = response.sessionId;
                this.sdk.nativeElement.startSessionDoc(
                  this.fortfaceFinishSession.bind(this),
                  response.sessionId,
                  response.sessionKey
                );
              });
            },
            (error) => {
              this.ngZone.run(() => {
                this.captureError.emit("Oopss! Ocorreu um erro no servidor, tente novamente.");
                console.error('Erro ao chamar a API:', error);
              });
            }
          );
      } catch (error: any) {
        this.ngZone.run(() => {
          this.handleStartError(error);
        });
      }
    });
  }

  /**
   * Trata os erros lançados pelo método startDoc() (v2.4.0+).
   */
  private handleStartError(error: any) {
    console.error('Erro ao inicializar a Fortface SDK (documento):', error);

    const errorCode = (error?.message || error?.code || error || '').toString();
    const matchedKey = Object.keys(START_ERROR_MESSAGES).find(key => errorCode.includes(key));
    const message = matchedKey
      ? START_ERROR_MESSAGES[matchedKey]
      : 'Erro ao inicializar a captura de documentos. Tente novamente.';

    this.captureError.emit(message);
  }

  fortfaceFinishSession(fortfaceSessionResult: FortFaceSdk.IFortfaceSessionResult) {
    const { action, data, sessionDetails } = fortfaceSessionResult;

    const handleResult = (data: FortFaceSdk.IFortfaceData) => {
      const encryptData = data.encryptData;
      const imageBase64 = DEFAULT_PHOTO;

      console.debug('[Fortface-doc] encryptData keys:', encryptData ? Object.keys(encryptData) : null);

      // A partir da v2.4.3, o SDK pode retornar `imgBestFrame` em vez de
      // `imgData` (ambos opcionais). Usamos qualquer um que estiver presente.
      const imgDataValue = encryptData?.imgData ?? (encryptData as any)?.imgBestFrame ?? "";

      const fortfaceDataResult: FortFaceDataDocument = {
        sessionId: this.sessionId,
        data: encryptData?.data ?? "",
        imgData: imgDataValue,
        key: encryptData?.key ?? "",
        imagePreview: imageBase64.replace('data:image/jpeg;base64,', ''),
        isLastCtps: this.campo.codigo?.includes("ctps.ultimoRegistro") ? true : false,
        isVerso: this.campo.codigo?.includes("verso") ? true : false
      }

      this.imageCapture.emit(fortfaceDataResult)
    }

    const handleCancel = () => {
      this.captureError.emit("Você cancelou a captura");
    }

    const handleTimeout = () => {
      this.captureError.emit("Você cancelou a captura");
    }

    const handleTimeoutReady = () => {
      this.captureError.emit("Captura cancelada automaticamente por timeout");
    }

    const handleError = (errorCode?: FortFaceSdk.FortfaceSessionErrorCode | string) => {
      const code = errorCode ? String(errorCode) : '';
      const message = SESSION_ERROR_MESSAGES[code] || (code ? `Erro na captura: ${code}` : 'Erro na captura');
      this.captureError.emit(message);
    }

    switch (action) {
      case 'capture':
        if (data) {
          handleResult(data);
        }
        break;
      case 'cancel':
        handleCancel();
        break;
      case 'timeout':
        handleTimeout();
        break;
      case 'timeout_ready':
        handleTimeoutReady();
        break;
      case 'error':
        handleError(sessionDetails?.errorCode);
        break;
      default:
        break;
    }
  }
}
